import React, { useRef, useState } from 'react';
import { api } from '@/lib/api';
import { Panel } from '@/components/Panel';
import { HeritageSeal, DahliaMark } from '@/components/BrandMark';
import { Printer, Clock, CheckCircle2, Copy, AlertCircle, Pause, CalendarClock, HelpCircle } from 'lucide-react';

function fmtDate(iso) {
  try {
    const d = iso ? new Date(iso) : new Date();
    return d.toLocaleDateString('en-NZ', { day: '2-digit', month: 'long', year: 'numeric' });
  } catch { return ''; }
}

function InvoiceRow({ label, value, testid }) {
  return (
    <div className="flex justify-between gap-4 py-2 border-b" style={{ borderColor: 'rgba(75,24,56,0.12)' }}>
      <span className="h-eyebrow text-[10px] whitespace-nowrap opacity-80">{label}</span>
      <span className="h-serif text-right" style={{ color: 'var(--heritage-plum)' }} data-testid={testid}>{value}</span>
    </div>
  );
}

function Copyable({ value, label }) {
  const [copied, setCopied] = React.useState(false);
  const copy = async (e) => {
    e.stopPropagation();
    try { await navigator.clipboard.writeText(value); setCopied(true); setTimeout(() => setCopied(false), 1500); } catch (err) { console.warn('[InvoiceView] clipboard write failed', err); }
  };
  return (
    <button type="button" onClick={copy}
            className="inline-flex items-center gap-1.5 text-xs opacity-70 hover:opacity-100 transition"
            style={{ color: 'var(--heritage-plum)' }}
            aria-label={`Copy ${label}`}>
      {copied ? <CheckCircle2 size={12} /> : <Copy size={12} />}
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

/**
 * InvoiceView
 * Displays a printable Heritage Apparel Founders Club invoice.
 * Props:
 *  - data: {
 *      founder_number, applicant_name, applicant_email,
 *      tier: { name, subtitle, price, currency, inclusions },
 *      payment: { id, amount, currency, created_at, status },
 *      bank_name, bank_account, company_name, company_country,
 *      status: 'pending' | 'paid'
 *    }
 *  - onPrimary: optional CTA button below the invoice (e.g. "Continue to Account" from JoinWizard)
 *  - primaryLabel: label for the primary CTA
 *  - showPrimary: whether to show the primary CTA
 */
export default function InvoiceView({ data, onPrimary, primaryLabel = 'Continue', showPrimary = false, canRequestPause = false, onPauseRequested }) {
  const printRef = useRef(null);
  const [pausing, setPausing] = useState(null);
  const [pauseError, setPauseError] = useState('');

  const doPrint = () => window.print();

  if (!data) return null;
  const tier = data.tier || {};
  const paid = data.status === 'paid' || data.payment_verified;
  const amount = data.payment?.amount ?? tier.price ?? 0;
  const currency = data.payment?.currency ?? tier.currency ?? 'NZD';
  const createdAt = data.payment?.created_at;
  const plan_type = data.payment?.plan_type || 'full';
  const plan_label = data.payment?.plan_label || 'Pay in full';
  const instalments = data.payment?.instalments || [];
  const isPlan = plan_type !== 'full' && instalments.length > 0;
  const paidCount = instalments.filter(i => i.status === 'paid').length;

  const fmtDue = (iso) => {
    try {
      const d = new Date(iso);
      return d.toLocaleDateString('en-NZ', { weekday: 'short', day: '2-digit', month: 'short' });
    } catch { return ''; }
  };

  const requestPause = async (index) => {
    setPauseError('');
    if (!window.confirm('Pause this instalment by 7 days? We only ask that you keep in touch.')) return;
    setPausing(index);
    try {
      await api.post('/founders/request-pause', {
        payment_id: data.payment?.id,
        instalment_index: index,
        days: 7,
      });
      if (onPauseRequested) onPauseRequested();
    } catch (e) {
      setPauseError(e?.response?.data?.detail || 'Could not pause. Please contact us directly.');
    } finally {
      setPausing(null);
    }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 invoice-container">
      {/* Status banner */}
      <div
        className="rounded-2xl border-2 px-5 py-4 flex items-start gap-3 no-print"
        style={{
          borderColor: paid ? 'rgba(143,163,75,0.5)' : 'rgba(75,24,56,0.35)',
          background: paid ? 'rgba(143,163,75,0.08)' : 'rgba(75,24,56,0.06)',
        }}
        data-testid="invoice-status-banner"
      >
        {paid ? (
          <CheckCircle2 size={22} className="flex-shrink-0 mt-0.5" style={{ color: '#5B6E2C' }} />
        ) : (
          <Clock size={22} className="flex-shrink-0 mt-0.5" style={{ color: 'var(--heritage-plum)' }} />
        )}
        <div>
          <div className="h-serif text-lg" style={{ color: paid ? '#5B6E2C' : 'var(--heritage-plum)' }}>
            {paid ? 'Payment received — welcome, Founder' : 'Awaiting payment verification'}
          </div>
          <div className="text-sm opacity-85 mt-0.5" style={{ color: 'var(--heritage-plum)' }}>
            {paid
              ? 'Your seat in the Founders Club is confirmed. Full console access is now available.'
              : "Your seat is reserved. Once we receive and verify your bank deposit, you will be granted full Founders Console access. Reference your Founder Number on the transfer so we can match it quickly."}
          </div>
        </div>
      </div>

      {/* Invoice paper */}
      <Panel className="p-8 md:p-10 invoice-paper" data-testid="invoice-paper">
        <div ref={printRef}>
          {/* Header */}
          <div className="flex items-start justify-between gap-4 pb-6 border-b" style={{ borderColor: 'rgba(75,24,56,0.2)' }}>
            <div className="flex items-center gap-3">
              <DahliaMark size={60} />
              <div>
                <div className="h-serif text-2xl tracking-[0.24em]" style={{ color: 'var(--heritage-plum)' }}>HERITAGE</div>
                <div className="h-eyebrow mt-1">Apparel &middot; Founders Club</div>
              </div>
            </div>
            <div className="text-right">
              <div className="h-eyebrow">Invoice</div>
              <div className="h-display text-2xl mt-1" style={{ color: 'var(--heritage-plum)' }}>
                {data.founder_number || '—'}
              </div>
              <div className="text-xs opacity-70 mt-1" style={{ color: 'var(--heritage-plum)' }}>
                {fmtDate(createdAt)}
              </div>
              <div className="mt-2">
                <span
                  className="h-chip inline-block"
                  style={{
                    background: paid ? 'rgba(143,163,75,0.15)' : 'rgba(75,24,56,0.08)',
                    borderColor: paid ? 'rgba(143,163,75,0.4)' : 'rgba(75,24,56,0.25)',
                    color: paid ? '#5B6E2C' : 'var(--heritage-plum)',
                  }}
                  data-testid="invoice-status-chip"
                >
                  {paid ? 'PAID' : 'AWAITING PAYMENT'}
                </span>
              </div>
            </div>
          </div>

          {/* Billed to + From */}
          <div className="grid sm:grid-cols-2 gap-6 py-6 border-b" style={{ borderColor: 'rgba(75,24,56,0.12)' }}>
            <div>
              <div className="h-eyebrow mb-2">Billed to</div>
              <div className="h-serif text-lg leading-tight" style={{ color: 'var(--heritage-plum)' }} data-testid="invoice-billed-to">
                {data.applicant_name || 'Founder'}
              </div>
              <div className="text-sm opacity-80" style={{ color: 'var(--heritage-plum)' }}>{data.applicant_email || ''}</div>
              <div className="text-xs opacity-70 mt-1" style={{ color: 'var(--heritage-plum)' }}>
                Founder Number: <span className="h-serif" style={{ color: 'var(--heritage-plum)' }}>{data.founder_number || '—'}</span>
              </div>
            </div>
            <div className="sm:text-right">
              <div className="h-eyebrow mb-2">From</div>
              <div className="h-serif text-lg leading-tight" style={{ color: 'var(--heritage-plum)' }}>
                {data.company_name || 'Heritage Dahlias NZ Limited'}
              </div>
              <div className="text-sm opacity-80" style={{ color: 'var(--heritage-plum)' }}>{data.company_country || 'Aotearoa, New Zealand'}</div>
              <div className="text-xs opacity-70 mt-1" style={{ color: 'var(--heritage-plum)' }}>
                hello@heritageapparel.co.nz
              </div>
            </div>
          </div>

          {/* Line item */}
          <div className="py-6 border-b" style={{ borderColor: 'rgba(75,24,56,0.12)' }}>
            <div className="h-eyebrow mb-3">Membership</div>
            <div className="rounded-xl p-5" style={{ background: '#FBF8F1', border: '1px solid rgba(75,24,56,0.15)' }}>
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <div className="h-serif text-xl leading-tight" style={{ color: 'var(--heritage-plum)' }} data-testid="invoice-tier-name">
                    {tier.name || 'Founders Club Tier'}
                  </div>
                  {tier.subtitle && (
                    <div className="text-sm italic opacity-80" style={{ color: 'var(--heritage-plum)' }}>{tier.subtitle}</div>
                  )}
                  {(tier.inclusions || []).length > 0 && (
                    <ul className="mt-3 space-y-1 text-sm" style={{ color: 'var(--heritage-plum)' }}>
                      {tier.inclusions.slice(0, 6).map((i) => (
                        <li key={i} className="flex gap-2"><span>◈</span><span className="opacity-90">{i}</span></li>
                      ))}
                    </ul>
                  )}
                </div>
                <div className="text-right shrink-0">
                  <div className="h-display text-3xl" style={{ color: 'var(--heritage-plum)' }} data-testid="invoice-amount">
                    ${Number(amount).toLocaleString()}
                  </div>
                  <div className="text-xs opacity-70" style={{ color: 'var(--heritage-plum)' }}>{currency}</div>
                </div>
              </div>
            </div>
          </div>

          {/* Payment plan schedule */}
          {isPlan && (
            <div className="py-6 border-b" style={{ borderColor: 'rgba(75,24,56,0.12)' }} data-testid="invoice-schedule">
              <div className="flex items-start justify-between gap-3 mb-3">
                <div>
                  <div className="h-eyebrow flex items-center gap-1.5"><CalendarClock size={12} /> Your Heritage Tab schedule</div>
                  <div className="h-serif text-lg mt-1" style={{ color: 'var(--heritage-plum)' }}>{plan_label}</div>
                </div>
                <span
                  className="h-chip"
                  style={{
                    background: 'rgba(154,75,126,0.12)',
                    borderColor: 'rgba(154,75,126,0.35)',
                    color: 'var(--heritage-plum)',
                  }}
                  data-testid="invoice-schedule-progress"
                >
                  {paidCount} of {instalments.length} paid
                </span>
              </div>
              <div className="rounded-xl overflow-hidden" style={{ border: '1px solid rgba(75,24,56,0.15)' }}>
                <table className="w-full text-sm">
                  <thead style={{ background: 'rgba(75,24,56,0.06)', color: 'var(--heritage-plum)' }}>
                    <tr>
                      <th className="text-left p-3 h-eyebrow text-[10px]">#</th>
                      <th className="text-left p-3 h-eyebrow text-[10px]">Due</th>
                      <th className="text-left p-3 h-eyebrow text-[10px]">Amount</th>
                      <th className="text-left p-3 h-eyebrow text-[10px]">Reference</th>
                      <th className="text-left p-3 h-eyebrow text-[10px]">Status</th>
                      {canRequestPause && !paid && <th className="text-left p-3 h-eyebrow text-[10px] no-print"></th>}
                    </tr>
                  </thead>
                  <tbody style={{ color: 'var(--heritage-plum)' }}>
                    {instalments.map((inst, i) => {
                      const isPaid = inst.status === 'paid';
                      return (
                        <tr key={inst.reference || `inst-${i}`} className="border-t" style={{ borderColor: 'rgba(75,24,56,0.1)' }} data-testid={`invoice-inst-row-${i}`}>
                          <td className="p-3 h-serif">{i + 1}</td>
                          <td className="p-3">{fmtDue(inst.due_date)}{inst.paused && <span className="ml-1 text-[10px] opacity-70">(paused)</span>}</td>
                          <td className="p-3 h-serif">${Number(inst.amount).toLocaleString()} {currency}</td>
                          <td className="p-3 text-xs opacity-80">{inst.reference}</td>
                          <td className="p-3">
                            <span
                              className="h-chip capitalize"
                              style={{
                                background: isPaid ? 'rgba(143,163,75,0.15)' : 'rgba(75,24,56,0.08)',
                                borderColor: isPaid ? 'rgba(143,163,75,0.4)' : 'rgba(75,24,56,0.25)',
                                color: isPaid ? '#5B6E2C' : 'var(--heritage-plum)',
                              }}
                            >
                              {isPaid ? 'Received' : 'Due'}
                            </span>
                          </td>
                          {canRequestPause && !paid && (
                            <td className="p-3 no-print">
                              {!isPaid && (
                                <button
                                  type="button"
                                  className="h-btn-outline text-xs"
                                  disabled={pausing === i || inst.paused}
                                  onClick={() => requestPause(i)}
                                  data-testid={`invoice-pause-${i}`}
                                >
                                  <Pause size={12} className="mr-1.5" />
                                  {inst.paused ? 'Paused' : (pausing === i ? '...' : 'Pause 7 days')}
                                </button>
                              )}
                            </td>
                          )}
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {pauseError && <div className="text-xs mt-2" style={{ color: '#9A2C2C' }}>{pauseError}</div>}
              <div className="mt-3 flex items-start gap-2 text-xs opacity-85" style={{ color: 'var(--heritage-plum)' }}>
                <HelpCircle size={12} className="flex-shrink-0 mt-0.5" />
                <p>
                  Your Bundle enters production once the final instalment is received. If life happens, request a 7 day pause on any upcoming payment (max 2 per plan). No judgement, no fees. We simply ask that you communicate.
                </p>
              </div>
            </div>
          )}

          {/* Totals */}
          <div className="py-6 border-b" style={{ borderColor: 'rgba(75,24,56,0.12)' }}>
            <div className="grid grid-cols-2 gap-1 max-w-sm ml-auto">
              <div className="h-eyebrow py-1">Subtotal</div>
              <div className="h-serif text-right" style={{ color: 'var(--heritage-plum)' }}>${Number(amount).toLocaleString()} {currency}</div>
              <div className="h-eyebrow py-1">Fees</div>
              <div className="h-serif text-right" style={{ color: 'var(--heritage-plum)' }}>$0.00</div>
              <div className="h-eyebrow py-1 border-t mt-1 pt-2" style={{ borderColor: 'rgba(75,24,56,0.2)' }}>Total due</div>
              <div className="h-display text-2xl text-right border-t mt-1 pt-2" style={{ color: 'var(--heritage-plum)', borderColor: 'rgba(75,24,56,0.2)' }} data-testid="invoice-total">
                ${Number(amount).toLocaleString()} {currency}
              </div>
            </div>
          </div>

          {/* Payment instructions */}
          <div className="pt-6">
            <div className="h-eyebrow mb-3">How to pay {paid ? '(receipt)' : ''}</div>
            <div className="rounded-xl p-5" style={{ background: '#FBF8F1', border: '1px solid rgba(75,24,56,0.15)' }}>
              <div className="text-sm mb-3" style={{ color: 'var(--heritage-plum)' }}>
                {paid ? (
                  <>Payment received on our end. Thank you for supporting Heritage Apparel from the ground up.</>
                ) : (
                  <>Please transfer <strong>${Number(amount).toLocaleString()} {currency}</strong> via direct bank deposit to the account below. Use your <strong>Founder Number as the reference</strong> so we can verify quickly. Access to your Founder Console unlocks the moment we confirm the deposit.</>
                )}
              </div>
              <InvoiceRow label="Payee" value={<span>{data.bank_name || 'HERITAGE DAHLIAS NZ LIMITED'} <Copyable value={data.bank_name || ''} label="payee" /></span>} testid="invoice-payee" />
              <InvoiceRow label="Account #" value={<span>{data.bank_account || '—'} <Copyable value={data.bank_account || ''} label="account" /></span>} testid="invoice-account" />
              <InvoiceRow label="Reference" value={<span>{data.founder_number || '—'} <Copyable value={data.founder_number || ''} label="reference" /></span>} testid="invoice-reference" />
              <InvoiceRow label="Amount" value={`$${Number(amount).toLocaleString()} ${currency}`} testid="invoice-amount-row" />
              {paid && (
                <InvoiceRow label="Paid on" value={fmtDate(data.payment?.paid_at || data.payment?.created_at)} />
              )}
            </div>
            {!paid && (
              <div className="mt-4 flex items-start gap-2 text-xs opacity-80" style={{ color: 'var(--heritage-plum)' }}>
                <AlertCircle size={12} className="flex-shrink-0 mt-0.5" />
                <p>
                  Card and online payment options will be enabled at the public store opening. For the Founders Club presale, all payments are via direct deposit and manually verified by our team, usually within 24 hours of deposit.
                </p>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="mt-8 pt-6 border-t text-center" style={{ borderColor: 'rgba(75,24,56,0.15)' }}>
            <HeritageSeal size={48} />
            <p className="text-xs opacity-70 mt-3" style={{ color: 'var(--heritage-plum)' }}>
              Grown in the field. Sewn with intention. Small batch heritage apparel from Aotearoa.
            </p>
          </div>
        </div>
      </Panel>

      {/* Actions (not printed) */}
      <div className="flex flex-wrap justify-center gap-2 no-print">
        <button type="button" className="h-btn-outline" onClick={doPrint} data-testid="invoice-print">
          <Printer size={14} className="mr-2" /> Print or save as PDF
        </button>
        {showPrimary && onPrimary && (
          <button type="button" className="h-btn" onClick={onPrimary} data-testid="invoice-primary">
            {primaryLabel}
          </button>
        )}
      </div>
    </div>
  );
}
